'use client';

import { CoreEvent, eventNames } from '@carvana/core-events';
import Button from '@carvana/ds-button';
import { useCallback } from 'react';
import useComposerOpenReady from '~/hooks/common/composer/useComposerOpenReady';

type OwnProps = {
  displayText?: string | null;
  inputPlaceholder: string;
  question?: string;
  slot?: React.ReactNode;
};

const SEND_ICON_PATH =
  'M7.29289 0.292893C7.68342 -0.0976311 8.31658 -0.0976311 8.70711 0.292893L15.7071 7.29289C16.0976 7.68342 16.0976 8.31658 15.7071 8.70711C15.3166 9.09763 14.6834 9.09763 14.2929 8.70711L9 3.41421V15C9 15.5523 8.55228 16 8 16C7.44772 16 7 15.5523 7 15V3.41421L1.70711 8.70711C1.31658 9.09763 0.683418 9.09763 0.292893 8.70711C-0.0976311 8.31658 -0.0976311 7.68342 0.292893 7.29289L7.29289 0.292893Z';

export default function Composer({
  displayText,
  inputPlaceholder,
  question,
  slot
}: OwnProps) {
  const { isPending, whenReady } = useComposerOpenReady();
  const isTyping = displayText !== null && displayText !== undefined;

  const handleOpen = useCallback(() => {
    if (isTyping) return;

    whenReady(() => {
      CoreEvent.emit(eventNames.composer.open, { question, source: 'page' });
    });
  }, [isTyping, question, whenReady]);

  return (
    <div
      className="flex flex-col gap-lg rounded-t-2xl bg-background-default p-xl shadow-[0_0_15px_0_rgba(106,115,125,0.16),0_0_3px_0_rgba(106,115,125,0.15)]"
      data-testid="composer"
    >
      {slot && (
        <div className="no-scrollbar flex min-h-[36px] w-full overflow-x-auto">
          <div className="flex w-full items-center gap-md">{slot}</div>
        </div>
      )}
      <Button
        aria-label="Open composer"
        className="flex w-full items-center gap-xl rounded-full border border-border-weak bg-background-default p-md text-left"
        data-analytics-id="composer_shell_opened"
        data-testid="composer-open"
        disabled={isTyping}
        isLoading={isPending}
        onClick={handleOpen}
        variant="tertiary"
      >
        <span className="flex-1 truncate text-text-weak t-body-md">
          {displayText || inputPlaceholder}
        </span>
        <span className="flex h-[2rem] w-[2rem] shrink-0 items-center justify-center rounded-full bg-background-primary text-text-inverse">
          <svg fill="none" height="16" viewBox="0 0 16 16" width="16">
            <path d={SEND_ICON_PATH} fill="currentColor" />
          </svg>
        </span>
      </Button>
    </div>
  );
}
